import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { DatePicker, Space, TimePicker, Typography } from '@duxton-web/ui';
import { Spin } from 'antd';
import dayjs from 'dayjs';
import localeData from 'dayjs/plugin/localeData';
import weekday from 'dayjs/plugin/weekday';
import { connect } from 'react-redux';

import { ControlledInputPropsBase } from '@/common/InputControlProps';
import { Availability, default as Service } from '@/lib/App';
import { fetchExperimentAvailability } from '@/store/state/availability/actions';
import {
  expAvailabilityLoadingStateSelector,
  expAvailabilitySelector,
} from '@/store/state/availability/selectors';
import { AppDispatch, AppState } from '@/store/types';

import './DurationSelector.css';

import customParseFormat from 'dayjs/plugin/customParseFormat';

import { getGeography } from '../../constants';

// Configure dayjs plugins required by DatePicker
dayjs.extend(weekday);
dayjs.extend(localeData);
dayjs.extend(customParseFormat);

const VERSION_TWO_TICKETING_PACKAGE = 2;
const VERSION_THREE_X_SCHEDULER = 3;
// Buffer time in minutes for default future time when selecting today's date
const CONSTANT_MINUTES = 7;

interface OwnProps
  extends ControlledInputPropsBase<[dayjs.Dayjs, dayjs.Dayjs]> {
  restrictBy?: string;
  domain?: string;
  experimentName?: string;
  isOverlap?: boolean;
  isExpTimeSliceStrategy?: boolean;
  mode?: 'single' | 'range';
}

interface StateProps {
  data?: Availability | undefined | null;
  isFetching: boolean;
}

interface DispatchProps {
  fetchExperimentAvailability: (
    domain: string,
    restrictBy: string,
    experimentName: string,
    version: number,
    isOverlap: boolean,
    isExpTimeSliceStrategy: boolean,
  ) => void;
}

type ExperimentFormProps = OwnProps & StateProps & DispatchProps;

const DurationSelector = ({
  value,
  onChange,
  data,
  isFetching,
  disabled,
  mode,
  domain,
  restrictBy,
  experimentName,
  fetchExperimentAvailability,
  isOverlap,
  isExpTimeSliceStrategy,
}: ExperimentFormProps) => {
  const [isDomainlessFeatureFlag, setIsDomainlessFeatureFlag] = useState(false);
  const [isDomainOverlapSupported, setIsDomainOverlapSupported] =
    useState(false);
  const [dateParts, setDateParts] = useState<
    Array<dayjs.Dayjs | null | undefined>
  >([]);
  const [timeParts, setTimeParts] = useState<
    Array<dayjs.Dayjs | null | undefined>
  >([]);

  const geography = getGeography(domain);

  // Initialization effect (constructor equivalent)
  useEffect(() => {
    const isDomainlessFlag = Service.cache.isFeatureFlagOn('domainless');
    const isDomainOverlapSupportedValue =
      Service.cache.isDomainOverlapSupported(domain);
    const version =
      isDomainlessFlag || isDomainOverlapSupportedValue || geography === 'multi'
        ? VERSION_THREE_X_SCHEDULER
        : VERSION_TWO_TICKETING_PACKAGE;

    setIsDomainlessFeatureFlag(isDomainlessFlag);
    setIsDomainOverlapSupported(isDomainOverlapSupportedValue);
    setDateParts([value?.[0] || undefined, value?.[1] || undefined]);
    setTimeParts([value?.[0] || undefined, value?.[1] || undefined]);

    if (domain && restrictBy) {
      fetchExperimentAvailability(
        domain,
        restrictBy,
        experimentName,
        version,
        isOverlap,
        isExpTimeSliceStrategy,
      );
    }
  }, []);

  // Effect for dependency changes (componentDidUpdate equivalent)
  useEffect(() => {
    const isDomainOverlapSupportedValue =
      Service.cache.isDomainOverlapSupported(domain!);
    const version =
      isDomainlessFeatureFlag ||
      isDomainOverlapSupportedValue ||
      geography === 'multi'
        ? VERSION_THREE_X_SCHEDULER
        : VERSION_TWO_TICKETING_PACKAGE;

    if (domain && restrictBy) {
      fetchExperimentAvailability(
        domain!,
        restrictBy!,
        experimentName!,
        version,
        isOverlap,
        isExpTimeSliceStrategy,
      );
    }
  }, [
    domain,
    restrictBy,
    isOverlap,
    isExpTimeSliceStrategy,
    isDomainlessFeatureFlag,
    experimentName,
    fetchExperimentAvailability,
  ]);

  // Effect for value changes
  useEffect(() => {
    const nextDateParts = [...dateParts];
    const nextTimeParts = [...timeParts];
    if (value?.[0]) {
      nextDateParts[0] = value[0];
      nextTimeParts[0] = value[0];
    }
    if (value?.[1]) {
      nextDateParts[1] = value[1];
      nextTimeParts[1] = value[1];
    }
    setDateParts(nextDateParts);
    setTimeParts(nextTimeParts);
  }, [value]);

  const renderDate = useCallback(
    (current: dayjs.Dayjs) => {
      let val = '';
      let status = '';
      let partial = false;
      if (data != null) {
        const dateStr = current?.format('YYYY-MM-DD');
        if (data[dateStr] !== undefined) {
          const avlInfo = data[dateStr];
          val = `${Math.round(avlInfo.availability * 100)}%`;
          status = avlInfo.status;
          partial = avlInfo.partialOccupancy;
        }
      }
      return (
        <div className="ant-picker-cell-inner">
          {current?.date()}
          {val !== '' && (
            <div className={`experiment-duration__availability ${status}`}>
              {val}
              <span className="experiment-duration__partial">
                {partial ? '*' : ''}
              </span>
            </div>
          )}
        </div>
      );
    },
    [data],
  );

  const disableDate = useCallback((current: dayjs.Dayjs | undefined) => {
    if (current == undefined) {
      return false;
    }
    return current < dayjs().startOf('day');
  }, []);

  const handleDateChange = useCallback(
    (index: number, newDate: dayjs.Dayjs | null) => {
      const nextDateParts = [...dateParts];
      const nextTimeParts = [...timeParts];
      nextDateParts[index] = newDate || undefined;

      const base = newDate || value[index] || undefined;
      if (base) {
        const time = nextTimeParts[index] || value[index] || undefined;
        let hours = 0;
        let minutes = 0;
        if (time) {
          hours = dayjs(time).hour();
          minutes = dayjs(time).minute();
        } else if (newDate && dayjs(newDate).isSame(dayjs(), 'day')) {
          const futureTime = dayjs().add(CONSTANT_MINUTES, 'minute');
          hours = futureTime.hour();
          minutes = futureTime.minute();
        }
        const composed = newDate
          ? dayjs(newDate).hour(hours).minute(minutes)
          : undefined;

        const newValue = [...value];
        newValue[index] = composed as any;
        setDateParts(nextDateParts);
        setTimeParts(nextTimeParts);
        onChange(newValue as [dayjs.Dayjs, dayjs.Dayjs]);
        return;
      }

      const newValue = [...value];
      newValue[index] = null;
      setDateParts(nextDateParts);
      setTimeParts(nextTimeParts);
      onChange(newValue as any);
    },
    [dateParts, timeParts, value, onChange],
  );

  const handleTimeChange = useCallback(
    (index: number, newTime: any) => {
      const nextDateParts = [...dateParts];
      const nextTimeParts = [...timeParts];

      const date = nextDateParts[index] || value[index] || undefined;
      const newValue = [...value];
      if (newTime && date) {
        nextTimeParts[index] = newTime;
        newValue[index] = dayjs(date)
          .hour(dayjs(newTime).hour())
          .minute(dayjs(newTime).minute());
      } else if (!newTime && date) {
        nextTimeParts[index] = undefined;
        nextDateParts[index] = date as any;
        newValue[index] = null as any;
      } else if (newTime && !date) {
        nextTimeParts[index] = newTime;
        setDateParts(nextDateParts);
        setTimeParts(nextTimeParts);
        return;
      } else {
        nextTimeParts[index] = undefined;
        newValue[index] = null;
      }
      setDateParts(nextDateParts);
      setTimeParts(nextTimeParts);
      onChange(newValue as any);
    },
    [dateParts, timeParts, value, onChange],
  );

  const renderOverlappingFooter = useCallback(() => {
    if (
      isDomainlessFeatureFlag ||
      isDomainOverlapSupported ||
      geography === 'multi'
    ) {
      return (
        <div
          style={{
            fontSize: '12px',
            padding: '6px',
            maxWidth: '320px',
            lineHeight: '1.2',
          }}
        >
          <div style={{ margin: '0 0 2px 0' }}>
            * Higher availability can be expected based on the time
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0px' }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                margin: '0',
                padding: '0',
              }}
            >
              <div
                style={{
                  backgroundColor: '#D9F7BE',
                  borderRadius: '3px',
                  height: '6px',
                  width: '6px',
                  marginTop: '1px',
                  marginRight: '4px',
                  flexShrink: 0,
                }}
              />
              <Typography.Text
                style={{
                  fontSize: '11px',
                  lineHeight: '1.1',
                  margin: '0',
                  padding: '0',
                }}
              >
                - 100% of the current population is available
              </Typography.Text>
            </div>
            <div
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                margin: '0',
                padding: '0',
              }}
            >
              <div
                style={{
                  backgroundColor: '#FFFB7F',
                  borderRadius: '3px',
                  height: '6px',
                  width: '6px',
                  marginTop: '1px',
                  marginRight: '4px',
                  flexShrink: 0,
                }}
              />
              <Typography.Text
                style={{
                  fontSize: '11px',
                  lineHeight: '1.1',
                  margin: '0',
                  padding: '0',
                }}
              >
                - Percentage of population available without additional approval
              </Typography.Text>
            </div>
            <div
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                margin: '0',
                padding: '0',
              }}
            >
              <div
                style={{
                  backgroundColor: '#FFCCC7',
                  borderRadius: '3px',
                  height: '6px',
                  width: '6px',
                  marginTop: '1px',
                  marginRight: '4px',
                  flexShrink: 0,
                }}
              />
              <Typography.Text
                style={{
                  fontSize: '11px',
                  lineHeight: '1.1',
                  margin: '0',
                  padding: '0',
                }}
              >
                - No population available for the experiment
              </Typography.Text>
            </div>
          </div>
        </div>
      );
    }
    return (
      <div
        style={{
          fontSize: '12px',
          padding: '6px',
          maxWidth: '320px',
          lineHeight: '1.2',
        }}
      >
        <div style={{ margin: '0 0 2px 0' }}>
          * Higher availability can be expected based on the time
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0px' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              margin: '0',
              padding: '0',
            }}
          >
            <div
              style={{
                backgroundColor: '#D9F7BE',
                borderRadius: '3px',
                height: '6px',
                width: '6px',
                marginTop: '1px',
                marginRight: '4px',
                flexShrink: 0,
              }}
            />
            <Typography.Text
              style={{
                fontSize: '11px',
                lineHeight: '1.1',
                margin: '0',
                padding: '0',
              }}
            >
              - 100% of the current population is available
            </Typography.Text>
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              margin: '0',
              padding: '0',
            }}
          >
            <div
              style={{
                backgroundColor: '#FFFB7F',
                borderRadius: '3px',
                height: '6px',
                width: '6px',
                marginTop: '1px',
                marginRight: '4px',
                flexShrink: 0,
              }}
            />
            <Typography.Text
              style={{
                fontSize: '11px',
                lineHeight: '1.1',
                margin: '0',
                padding: '0',
              }}
            >
              - Percentage of population available without approval, any
              percentage above will require approval from overlapping
              experiments
            </Typography.Text>
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              margin: '0',
              padding: '0',
            }}
          >
            <div
              style={{
                backgroundColor: '#FFD591',
                borderRadius: '3px',
                height: '6px',
                width: '6px',
                marginTop: '1px',
                marginRight: '4px',
                flexShrink: 0,
              }}
            />
            <Typography.Text
              style={{
                fontSize: '11px',
                lineHeight: '1.1',
                margin: '0',
                padding: '0',
              }}
            >
              - Percentage of the current population is available but needs
              approval from overlapping experiments. Any percentage above will
              exceed our total population
            </Typography.Text>
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              margin: '0',
              padding: '0',
            }}
          >
            <div
              style={{
                backgroundColor: '#FFCCC7',
                borderRadius: '3px',
                height: '6px',
                width: '6px',
                marginTop: '1px',
                marginRight: '4px',
                flexShrink: 0,
              }}
            />
            <Typography.Text
              style={{
                fontSize: '11px',
                lineHeight: '1.1',
                margin: '0',
                padding: '0',
              }}
            >
              - No population available for the experiment
            </Typography.Text>
          </div>
        </div>
      </div>
    );
  }, [isDomainlessFeatureFlag, isDomainOverlapSupported, geography]);

  return (
    <>
      {/* Render logic */}
      {mode === 'single' ? (
        <div style={{ width: '100%' }}>
          <Space.Compact style={{ display: 'flex', width: '100%' }}>
            <DatePicker
              placeholder="Select Date"
              value={dateParts[0] ? dayjs(dateParts[0]) : value[0] || null}
              onChange={(date) => handleDateChange(0, date)}
              disabled={disabled}
              className="cy-experiment-duration-date"
              popupClassName={data ? 'experiment-duration' : ''}
              disabledDate={disableDate}
              cellRender={renderDate}
              renderExtraFooter={renderOverlappingFooter}
            />
            <TimePicker
              placeholder="Select Time"
              format="HH:mm [GMT +8]"
              value={timeParts[0] ? dayjs(timeParts[0]) : value[0] || null}
              onChange={(time) => handleTimeChange(0, time)}
              disabled={disabled}
              size="middle"
              className="cy-experiment-duration-time"
              allowClear
              needConfirm={false}
            />
          </Space.Compact>
          {isFetching && (
            <Spin
              size="small"
              className="experiment-duration-spinner"
              style={{ marginLeft: 8 }}
            />
          )}
        </div>
      ) : (
        <div className="duration-selector" style={{ width: '100%' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {/* Start Date */}
            <DatePicker
              placeholder="Start Date"
              value={dateParts[0] ? dayjs(dateParts[0]) : value[0] || null}
              onChange={(date) => handleDateChange(0, date)}
              disabled={disabled}
              className="cy-experiment-duration-start-date"
              popupClassName={data ? 'experiment-duration' : ''}
              disabledDate={disableDate}
              cellRender={renderDate}
              renderExtraFooter={renderOverlappingFooter}
            />

            {/* Start Time */}
            <TimePicker
              placeholder="Start Time"
              format="HH:mm [GMT +8]"
              value={timeParts[0] ? dayjs(timeParts[0]) : value[0] || null}
              onChange={(time) => handleTimeChange(0, time)}
              disabled={disabled}
              size="middle"
              className="cy-experiment-duration-start-time"
              allowClear
              needConfirm={false}
            />

            {/* Separator */}
            <Typography.Text
              style={{ fontSize: '16px', color: '#666', margin: '0 4px' }}
            >
              -
            </Typography.Text>

            {/* End Date */}
            <DatePicker
              placeholder="End Date"
              value={dateParts[1] ? dayjs(dateParts[1]) : value[1] || null}
              onChange={(date) => handleDateChange(1, date)}
              disabled={disabled}
              className="cy-experiment-duration-end-date"
              popupClassName={data ? 'experiment-duration' : ''}
              disabledDate={disableDate}
              cellRender={renderDate}
              renderExtraFooter={renderOverlappingFooter}
            />

            {/* End Time */}
            <TimePicker
              placeholder="End Time"
              format="HH:mm [GMT +8]"
              value={timeParts[1] ? dayjs(timeParts[1]) : value[1] || null}
              onChange={(time) => handleTimeChange(1, time)}
              disabled={disabled}
              size="middle"
              className="cy-experiment-duration-end-time"
              allowClear
              needConfirm={false}
            />

            {/* Spinner */}
            {isFetching && (
              <Spin size="small" className="experiment-duration-spinner" />
            )}
          </div>
        </div>
      )}
    </>
  );
};

function mapDispatchToProps(dispatch: AppDispatch): DispatchProps {
  return {
    fetchExperimentAvailability: (
      domain: string,
      restrictBy: string,
      experimentName: string,
      version: number,
      isOverlap: boolean,
      isExpTimeSliceStrategy: boolean,
    ) =>
      dispatch(
        fetchExperimentAvailability(
          domain,
          restrictBy,
          experimentName,
          version,
          isOverlap,
          isExpTimeSliceStrategy,
        ),
      ),
  };
}

function mapStateToProps(state: AppState, props: OwnProps): StateProps {
  return {
    data: expAvailabilitySelector(state, props.domain!, props.restrictBy!),
    isFetching: expAvailabilityLoadingStateSelector(
      state,
      props.domain!,
      props.restrictBy!,
    ),
  };
}

export default connect<StateProps, DispatchProps, OwnProps, AppState>(
  mapStateToProps,
  mapDispatchToProps,
)(DurationSelector);
